import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  HiOutlineLocationMarker, HiOutlineChat, HiOutlineBan, HiOutlineFlag,
  HiX, HiOutlinePhotograph, HiOutlineLockClosed, HiOutlineLockOpen,
  HiCheck, HiOutlineGift, HiOutlineSparkles, HiOutlineHome, HiOutlineTruck,
} from 'react-icons/hi';
import { profileAPI, blockAPI, conversationAPI, authAPI, photoAPI, videoCallAPI, creatorAPI, reportAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import VerifiedBadge from '../components/VerifiedBadge';
import LoadingAnimation from '../components/LoadingAnimation';
import './PublicProfile.css';

const PublicProfile = () => {
  const { userId } = useParams();
  const { userId: currentUserId, emailVerified } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockError, setUnlockError] = useState(null);
  const [unlockSuccess, setUnlockSuccess] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [initiatingCall, setInitiatingCall] = useState(false);
  const [callError, setCallError] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageError, setMessageError] = useState('');
  const [content, setContent] = useState([]);

  useEffect(() => {
    // Redirect if viewing own profile
    if (userId === currentUserId?.toString()) {
      navigate('/profile');
      return;
    }
    
    fetchProfile();
    checkBlockStatus();
  }, [userId, currentUserId]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await profileAPI.getUserProfile(userId);
      console.log('=== FRONTEND PROFILE DATA ===');
      console.log('Full response:', response.data);
      console.log('Has photos array?', !!response.data.photos);
      console.log('Photos array:', response.data.photos);
      console.log('Has private photos?', response.data.hasPrivatePhotos);
      console.log('Private photo count:', response.data.privatePhotoCount);
      
      if (response.data.photos && response.data.photos.length > 0) {
        response.data.photos.forEach((photo, index) => {
          console.log(`Photo ${index}:`, {
            id: photo.id,
            isPrivate: photo.isPrivate,
            isLocked: photo.isLocked,
            url: photo.url ? photo.url.substring(0, 50) + '...' : 'no url'
          });
        });
      }
      console.log('============================');
      
      setProfile(response.data);
      
      // If this is a creator, redirect to creator page instead
      if (response.data.isCreator) {
        navigate(`/creator/${userId}`);
        return;
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const checkBlockStatus = async () => {
    try {
      const response = await blockAPI.checkBlocked(userId);
      setIsBlocked(response.data.blocked);
    } catch (err) {
      console.error('Failed to check block status:', err);
    }
  };

  const handleSendVerificationOTP = async () => {
    setSendingOTP(true);
    try {
      await authAPI.sendEmailOTP(currentUserId);
      setSendingOTP(false);
      // Navigate to verification page
      navigate('/verify-email', { state: { from: `/profile/${userId}` } });
      return true;
    } catch (err) {
      console.error('Failed to send OTP:', err);
      setSendingOTP(false);
      return false;
    }
  };

  const handleSendMessage = async () => {
    // For all users, free messaging (creators are redirected to creator page)
    try {
      setSendingMessage(true);
      const response = await conversationAPI.getOrCreateConversation(userId);
      const conversationId = response.data.id;
      navigate(`/chat/${conversationId}`);
    } catch (err) {
      console.error('Failed to start conversation:', err);
      alert('Failed to start conversation. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleBlock = async () => {
    if (!window.confirm('Are you sure you want to block this user?')) {
      return;
    }

    try {
      setBlocking(true);
      await blockAPI.blockUser(userId, 'Blocked from profile');
      setIsBlocked(true);
      alert('User blocked successfully');
      navigate('/discover');
    } catch (err) {
      console.error('Failed to block user:', err);
      alert('Failed to block user');
    } finally {
      setBlocking(false);
    }
  };

  const handleReport = async () => {
    const reasons = [
      { value: 'HARASSMENT', label: 'Harassment or bullying' },
      { value: 'FAKE_PROFILE', label: 'Fake profile or impersonation' },
      { value: 'INAPPROPRIATE_CONTENT', label: 'Inappropriate content' },
      { value: 'SPAM', label: 'Spam or misleading' },
      { value: 'SCAM', label: 'Scam or fraud' },
      { value: 'OTHER', label: 'Other' }
    ];
    
    const reasonText = reasons.map((r, i) => `${i + 1}. ${r.label}`).join('\n');
    const choice = prompt(`Report ${profile.name}?\n\nSelect reason (1-6):\n${reasonText}`);
    
    if (!choice) return;
    
    const index = parseInt(choice) - 1;
    if (index < 0 || index >= reasons.length) {
      alert('Invalid selection');
      return;
    }
    
    const reason = reasons[index].value;
    const description = prompt('Please provide additional details (optional):');
    
    try {
      await reportAPI.reportUser(userId, reason, description || '');
      alert('Thank you for your report. Our team will review it within 24 hours.');
    } catch (err) {
      console.error('Failed to submit report:', err);
      alert(err.response?.data?.error || 'Failed to submit report. You may have already reported this user.');
    }
  };

  const getLastSeenText = (lastActive, isOnline) => {
    // If user is currently online (backend checks if active within last 2 minutes)
    if (isOnline) return 'Active now';
    
    if (!lastActive) return 'Never active';
    
    const now = new Date();
    // Parse as UTC timestamp - backend sends UTC, add 'Z' if not present
    const lastActiveStr = lastActive.endsWith('Z') ? lastActive : lastActive + 'Z';
    const lastActiveDate = new Date(lastActiveStr);
    const diffMs = now - lastActiveDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    // Show minutes for anything under 1 hour
    if (diffMins < 60) return `Active ${diffMins}m ago`;
    
    // Show hours for anything under 24 hours
    if (diffHours < 24) return `Active ${diffHours}h ago`;
    
    // Show "Yesterday" for 1 day ago
    if (diffDays === 1) return 'Active yesterday';
    
    // Show days for anything under 7 days
    if (diffDays < 7) return `Active ${diffDays}d ago`;
    
    // Show date for anything older
    return `Last seen ${lastActiveDate.toLocaleDateString()}`;
  };

  const getMoodEmoji = (mood) => {
    const moods = {
      'CHAT': '💬',
      'HOOKUP': '🔥',
      'FWB': '😈',
      'VIBING': '🎉',
    };
    return moods[mood] || '😊';
  };

  const getMoodLabel = (mood) => {
    const moods = {
      'CHAT': 'Want to chat',
      'HOOKUP': 'Want a hookup',
      'FWB': 'Friends with benefits',
      'VIBING': 'Just vibing',
    };
    return moods[mood] || 'No mood set';
  };

  const handleUnblock = async () => {
    try {
      setBlocking(true);
      await blockAPI.unblockUser(userId);
      setIsBlocked(false);
      alert('User unblocked successfully');
    } catch (err) {
      console.error('Failed to unblock user:', err);
      alert('Failed to unblock user');
    } finally {
      setBlocking(false);
    }
  };

  const handleInitiateCall = async () => {
    try {
      setInitiatingCall(true);
      setCallError(null);
      
      // First check if user already has an active call
      try {
        const activeCallResponse = await videoCallAPI.getActiveCall();
        if (activeCallResponse.data && activeCallResponse.data.id) {
          setCallError('You already have an active call. Please end it before starting a new one.');
          setInitiatingCall(false);
          return;
        }
      } catch (err) {
        // No active call, continue
        if (err.response?.status !== 404 && !err.response?.data?.message?.includes('No active call')) {
          console.error('Error checking active call:', err);
        }
      }
      
      const response = await videoCallAPI.initiateCall(userId);
      
      // Close modal
      setShowCallModal(false);
      
      // Navigate to call screen
      navigate(`/call/${response.data.id}`);
    } catch (err) {
      console.error('Failed to initiate call:', err);
      let errorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to initiate call';
      
      // Improve error message for conversation requirement
      if (errorMsg.includes('conversation') || errorMsg.includes('message first')) {
        errorMsg = 'Please send a message first before starting a video call. This helps establish a connection.';
      }
      
      setCallError(errorMsg);
    } finally {
      setInitiatingCall(false);
    }
  };

  const handleUnlockPrivatePhotos = async () => {
    // PHASE 1: All photos are free - no unlock needed
    setUnlockSuccess(true);
    setTimeout(() => {
      setShowUnlockModal(false);
      setUnlockSuccess(false);
    }, 1500);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const openFullscreen = (imageUrl) => {
    // Find the index of this image in the photos array
    const index = profile.photos.findIndex(p => p.url === imageUrl);
    setCurrentImageIndex(index >= 0 ? index : 0);
    setFullscreenImage(imageUrl);
  };

  const closeFullscreen = () => {
    setFullscreenImage(null);
    setCurrentImageIndex(0);
  };

  const goToNextImage = () => {
    if (!profile.photos || profile.photos.length === 0) return;
    
    let nextIndex = currentImageIndex + 1;
    // Skip locked images
    while (nextIndex < profile.photos.length && profile.photos[nextIndex].isLocked === true) {
      nextIndex++;
    }
    
    if (nextIndex < profile.photos.length) {
      setCurrentImageIndex(nextIndex);
      setFullscreenImage(profile.photos[nextIndex].url);
    }
  };

  const goToPreviousImage = () => {
    if (!profile.photos || profile.photos.length === 0) return;
    
    let prevIndex = currentImageIndex - 1;
    // Skip locked images
    while (prevIndex >= 0 && profile.photos[prevIndex].isLocked === true) {
      prevIndex--;
    }
    
    if (prevIndex >= 0) {
      setCurrentImageIndex(prevIndex);
      setFullscreenImage(profile.photos[prevIndex].url);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      closeFullscreen();
    } else if (e.key === 'ArrowRight') {
      goToNextImage();
    } else if (e.key === 'ArrowLeft') {
      goToPreviousImage();
    }
  };

  useEffect(() => {
    if (fullscreenImage) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [fullscreenImage]);

  if (loading) {
    return (
      <div className="public-profile-container">
        <LoadingAnimation />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="public-profile-container">
        <div className="error-state">
          <h2>Profile Not Found</h2>
          <p>{error || 'This profile does not exist or is no longer available'}</p>
          <button className="btn btn-primary" onClick={() => navigate('/discover')}>
            Back to Browse
          </button>
        </div>
      </div>
    );
  }

  if (isBlocked) {
    return (
      <div className="public-profile-container">
        <div className="blocked-state">
          <h2>User Blocked</h2>
          <p>You have blocked this user</p>
          <button className="btn btn-secondary" onClick={handleUnblock} disabled={blocking}>
            {blocking ? 'Unblocking...' : 'Unblock User'}
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/discover')}>
            Back to Browse
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="public-profile-container of-style">
      <button className="back-button" onClick={() => navigate('/discover')}>
        ← Back
      </button>

      {/* Cover Photo Section */}
      <div className="profile-cover-section">
        {profile.coverPhotoUrl ? (
          <img src={profile.coverPhotoUrl} alt="Cover" className="cover-photo" />
        ) : (
          <div className="cover-photo-placeholder"></div>
        )}
      </div>

      {/* Profile Header */}
      <div className="profile-header-of">
        <div className="profile-avatar-section">
          {profile.photos && profile.photos.length > 0 ? (
            <img 
              src={profile.photos[0].url} 
              alt={profile.name}
              className="profile-avatar-of"
            />
          ) : (
            <div className="profile-avatar-placeholder-of">
              {getInitials(profile.name)}
            </div>
          )}
        </div>

        <div className="profile-info-of">
          <div className="profile-name-row-of">
            <h1>
              {profile.name || 'Anonymous'}{profile.age ? `, ${profile.age}` : ''}
              {profile.hasVerifiedBadge && <VerifiedBadge size="large" />}
            </h1>
            {profile.isOnline && (
              <span className="online-badge-of">
                <span className="online-dot"></span>
                Online
              </span>
            )}
          </div>
          
          <div className="profile-meta-of">
            <span className="meta-item-of" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <HiOutlineLocationMarker aria-hidden="true" /> {profile.location}
            </span>
            {profile.distance && (
              <span className="meta-item-of">
                • {profile.distance.toFixed(1)} km away
              </span>
            )}
            <span className="meta-item-of">
              • {getLastSeenText(profile.lastActive, profile.isOnline)}
            </span>
          </div>

          {profile.currentMood && (
            <div className="mood-badge-of">
              {getMoodEmoji(profile.currentMood)} {getMoodLabel(profile.currentMood)}
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons Row */}
      <div className="action-buttons-of">
        <button 
          className="btn-action-of btn-message-of"
          onClick={handleSendMessage}
          disabled={sendingMessage}
        >
          {sendingMessage ? (
            <>
              <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" opacity="0.25"/>
                <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
              </svg>
              Opening...
            </>
          ) : (
            <>
              <HiOutlineChat aria-hidden="true" /> Message
            </>
          )}
        </button>
        
        {/* Video call button removed from public profile - calls start from chat (WhatsApp-style) */}
        
        <button 
          className="btn-action-of btn-block-of"
          onClick={handleBlock}
          disabled={blocking}
        >
          <HiOutlineBan aria-hidden="true" /> Block
        </button>
        
        <button 
          className="btn-action-of btn-report-of"
          onClick={handleReport}
          title="Report user"
        >
          <HiOutlineFlag aria-hidden="true" /> Report
        </button>
      </div>

      {/* Photo Gallery Grid */}
      {fullscreenImage && profile.photos && (
        <div className="fullscreen-modal" onClick={closeFullscreen}>
          <button className="fullscreen-close" onClick={closeFullscreen} aria-label="Close"><HiX /></button>
          
          {currentImageIndex > 0 && (
            <button 
              className="fullscreen-nav fullscreen-prev" 
              onClick={(e) => {
                e.stopPropagation();
                goToPreviousImage();
              }}
            >
              ‹
            </button>
          )}
          
          {currentImageIndex < profile.photos.filter(p => !p.isLocked).length - 1 && (
            <button 
              className="fullscreen-nav fullscreen-next" 
              onClick={(e) => {
                e.stopPropagation();
                goToNextImage();
              }}
            >
              ›
            </button>
          )}
          
          <img src={fullscreenImage} alt="Fullscreen view" onClick={(e) => e.stopPropagation()} />
          
          <div className="fullscreen-counter">
            {currentImageIndex + 1} / {profile.photos.filter(p => !p.isLocked).length}
          </div>
        </div>
      )}

      {/* Photo Gallery Grid */}
      {profile.photos && profile.photos.length > 0 && (
        <div className="photo-gallery-of">
          <h3 className="section-title-of" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><HiOutlinePhotograph aria-hidden="true" /> Photos</h3>
          <div className="photos-grid-of">
            {profile.photos.map((photo, index) => {
              const shouldBlur = photo.isLocked === true;
              const canClick = !shouldBlur;
              const isPlaceholder = photo.url && photo.url.includes('/placeholder/locked');
              
              return (
                <div 
                  key={photo.id || index} 
                  className="photo-item-of"
                  onClick={(e) => {
                    if (shouldBlur) {
                      setShowUnlockModal(true);
                    } else if (canClick) {
                      openFullscreen(photo.url);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {isPlaceholder ? (
                    <div className="locked-photo-placeholder-of">
                      <span className="lock-icon-large" aria-hidden="true"><HiOutlineLockClosed /></span>
                      <span className="locked-text">PRIVATE</span>
                    </div>
                  ) : (
                    <img 
                      src={photo.url} 
                      alt={`${profile.name || 'User'} - Photo ${index + 1}`}
                      className={shouldBlur ? 'blurred-image' : ''}
                      style={{ filter: shouldBlur ? 'blur(20px)' : 'none' }}
                    />
                  )}
                  {shouldBlur && !isPlaceholder && (
                    <div className="photo-locked-overlay-of">
                      <span className="lock-icon" aria-hidden="true"><HiOutlineLockClosed /></span>
                      <span className="unlock-text">Tap to Unlock</span>
                    </div>
                  )}
                  {index === 0 && !shouldBlur && (
                    <div className="photo-badge-of">MAIN</div>
                  )}
                </div>
              );
            })}
          </div>
          
          {profile.hasPrivatePhotos && profile.photos.some(p => p.isLocked) && (
            <button 
              className="btn-unlock-all-of"
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              onClick={() => setShowUnlockModal(true)}
            >
              <HiOutlineLockOpen aria-hidden="true" /> Unlock {profile.privatePhotoCount} Private Photo{profile.privatePhotoCount > 1 ? 's' : ''} (10 Credits)
            </button>
          )}
        </div>
      )}

      {/* About & Details Section */}
      <div className="content-sections-of">
        {/* Bio */}
        {profile.bio && (
          <div className="section-card-of">
            <h3>About</h3>
            <p>{profile.bio}</p>
          </div>
        )}

        {/* Services */}
        {profile.services && (
          <div className="section-card-of">
            <h3>Services</h3>
            <p>{profile.services}</p>
          </div>
        )}

        {/* Interests */}
        {profile.interests && (
          <div className="section-card-of">
            <h3>Interests</h3>
            <div className="interests-tags-of">
              {profile.interests.split(',').map((interest, index) => (
                <span key={index} className="interest-tag-of">
                  {interest.trim()}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Travel & Hosting */}
        {(profile.availableForTravel || profile.canHost || profile.hasParking) && (
          <div className="section-card-of">
            <h3>Availability</h3>
            <div className="availability-badges-of">
              {profile.availableForTravel && (
                <div className="badge-of" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><HiOutlineTruck aria-hidden="true" /> Can Travel</div>
              )}
              {profile.canHost && (
                <div className="badge-of" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><HiOutlineHome aria-hidden="true" /> Can Host</div>
              )}
              {profile.hasParking && (
                <div className="badge-of" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><HiOutlineLocationMarker aria-hidden="true" /> Parking Available</div>
              )}
            </div>
          </div>
        )}

        {/* Details */}
        <div className="section-card-of">
          <h3>Details</h3>
          <div className="details-grid-of">
            {profile.height && (
              <div className="detail-row-of">
                <span className="detail-label-of">Height</span>
                <span className="detail-value-of">{profile.height} cm</span>
              </div>
            )}
            {profile.bodyType && (
              <div className="detail-row-of">
                <span className="detail-label-of">Body Type</span>
                <span className="detail-value-of">{profile.bodyType}</span>
              </div>
            )}
            {profile.ethnicity && (
              <div className="detail-row-of">
                <span className="detail-label-of">Ethnicity</span>
                <span className="detail-value-of">{profile.ethnicity}</span>
              </div>
            )}
            {profile.bust && (
              <div className="detail-row-of">
                <span className="detail-label-of">Bust</span>
                <span className="detail-value-of">{profile.bust}</span>
              </div>
            )}
            {profile.eyeColor && (
              <div className="detail-row-of">
                <span className="detail-label-of">Eyes</span>
                <span className="detail-value-of">{profile.eyeColor}</span>
              </div>
            )}
            {profile.hairColor && (
              <div className="detail-row-of">
                <span className="detail-label-of">Hair</span>
                <span className="detail-value-of">{profile.hairColor}</span>
              </div>
            )}
            <div className="detail-row-of">
              <span className="detail-label-of">Gender</span>
              <span className="detail-value-of">{profile.gender}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Image Modal */}
      {fullscreenImage && profile.photos && (
        <div className="fullscreen-modal" onClick={closeFullscreen}>
          <button className="fullscreen-close" onClick={closeFullscreen} aria-label="Close"><HiX /></button>
          
          {currentImageIndex > 0 && (
            <button 
              className="fullscreen-nav fullscreen-prev" 
              onClick={(e) => {
                e.stopPropagation();
                goToPreviousImage();
              }}
            >
              ‹
            </button>
          )}
          
          {currentImageIndex < profile.photos.filter(p => !p.isLocked).length - 1 && (
            <button 
              className="fullscreen-nav fullscreen-next" 
              onClick={(e) => {
                e.stopPropagation();
                goToNextImage();
              }}
            >
              ›
            </button>
          )}
          
          <img src={fullscreenImage} alt="Fullscreen view" onClick={(e) => e.stopPropagation()} />
          
          <div className="fullscreen-counter">
            {currentImageIndex + 1} / {profile.photos.filter(p => !p.isLocked).length}
          </div>
        </div>
      )}

      {/* Unlock Modal */}
      {showUnlockModal && (
        <div className="modal-overlay" onClick={() => setShowUnlockModal(false)}>
          <div className="modal-content unlock-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowUnlockModal(false)} aria-label="Close"><HiX /></button>
            
            {unlockSuccess ? (
              <div className="unlock-success-message">
                <span className="success-icon" aria-hidden="true"><HiCheck /></span>
                <h2>All Photos Available!</h2>
                <p>All photos are free to view in Phase 1</p>
              </div>
            ) : (
              <>
                <div className="unlock-modal-header">
                  <span className="unlock-modal-icon" aria-hidden="true"><HiOutlineSparkles /></span>
                  <h2>All Photos Are Free!</h2>
                  <p>PHASE 1: View all of {profile.name}'s photos for free</p>
                </div>

                <div className="unlock-modal-body">
                  <div className="unlock-option-card">
                    <div className="unlock-option-icon" aria-hidden="true"><HiOutlineGift /></div>
                    <h3>Phase 1 - Free Access</h3>
                    <div className="unlock-price">FREE</div>
                    <p>All photos are now free to view! Enjoy browsing.</p>
                    <button 
                      className="btn btn-primary btn-block"
                      onClick={handleUnlockPrivatePhotos}
                      disabled={unlocking}
                    >
                      {unlocking ? 'Loading...' : 'View All Photos'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Video call modal removed - calls start from chat (WhatsApp-style) */}
    </div>
  );
};

export default PublicProfile;
